import { useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import { parseCsv } from '../../../lib/csv-table';
import { Card, ScrollArea, Typography } from '../ui';
import styles from './DocViewerModal.module.css';

// A CSV/TSV sheet as a real table. Before this, a `.csv` was `kind === null` and the chip navigated to the
// signed URL — where the browser either downloaded it or rendered one unwrapped line per record.
//
// The parse lives in `lib/csv-table.ts` (pure, unit-tested); this file is only presentation. Two decisions
// worth not undoing:
//
//   - CELLS DO NOT WRAP. They are `nowrap` + ellipsis with the full value in `title`, and the table scrolls
//     horizontally — which is how every spreadsheet preview behaves, and the only way a 30-column sheet
//     stays readable. (`@media print` in globals.css re-enables wrapping, because paper cannot scroll.)
//   - ONE settled `dir` for the whole table, from `messageDir` — the same helper `MarkdownDocView` uses and
//     the same reasoning: a sheet is one document, so an RTL sheet puts its first column on the right and
//     lets Latin/number cells embed as LTR runs. Never `dir="auto"` per cell, which is per-cell guessing
//     (see `test/doc-viewer-ux.test.ts`).
export function CsvDocView({ text }: { text: string }) {
  const { t } = useTranslation();
  // Parsing walks the whole document, so it must not re-run on every unrelated re-render of the viewer.
  const table = useMemo(() => parseCsv(text), [text]);
  const dir = messageDir(text);

  if (table.columnCount === 0) {
    return (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary" component="p">
          {t('docViewer.csvEmpty')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.csvBody}>
      {table.truncated && (
        <Typography variant="p3" color="secondary" component="p">
          {t('docViewer.csvTruncated', { count: table.rows.length })}
        </Typography>
      )}
      {/* `disablePadding` is the documented case for a Card holding edge-to-edge content — see CLAUDE.md
          "Cards". The flex sizing belongs to the call site's class, since Card never sets a height. */}
      <Card variant="outline" size="sm" disablePadding className={styles.csvCard}>
        <ScrollArea axis="both" className={styles.csvScroll}>
          <table className={styles.csvTable} dir={dir}>
            {table.header && (
              <thead>
                <tr>
                  {table.header.map((cell, column) => (
                    // The parser pads a short header, so a cell can legitimately be empty and the index is
                    // the only stable key — a sheet may repeat a column name.
                    <th key={column} scope="col" title={cell}>
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, column) => (
                    <td key={column} title={cell}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </Card>
    </div>
  );
}
